import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { memberApi } from '@formosa/shared/api/member';
import { ApiError } from '@formosa/shared/api/request';
import {
  GOOGLE_CLIENT_ID,
  getInviterCode,
} from '@formosa/shared/constants/auth';
import { mapGoogleLocale } from '@formosa/shared/utils/googleLocale';
import { useAuth } from '../../auth/AuthContext';
import { useTranslation } from '../../i18n/LocaleContext';

/**
 * Google 第三方登入（GSI id_token → registerLoginGoogleCallback）
 * 可見按鈕使用 App i18n；透明 GSI 按鈕疊在上層處理實際 OAuth 流程。
 * @param {'login'|'register'} mode 登入或註冊文案
 */
export default function GoogleSignInButton({ onError, mode = 'login' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginSuccess } = useAuth();
  const { locale, t } = useTranslation();
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [gsiReady, setGsiReady] = useState(false);

  const from = location.state?.from?.pathname || '/';
  const isRegister = mode === 'register';
  const label = isRegister ? t('auth.googleSignUp') : t('auth.googleSignIn');
  const gsiButtonText = isRegister ? 'signup_with' : 'signin_with';

  const handleGoogleResponse = useCallback(
    async (response) => {
      if (!response?.credential) {
        onError?.(t('auth.googleCredentialMissing'));
        return;
      }
      setLoading(true);
      onError?.('');
      try {
        const data = await memberApi.googleCallback({
          idToken: response.credential,
          inviterCode: getInviterCode() || undefined,
        });
        loginSuccess(data);
        navigate(from, { replace: true });
      } catch (err) {
        const message =
          err instanceof ApiError ? err.message : t('auth.googleLoginFailed');
        onError?.(message);
      } finally {
        setLoading(false);
      }
    },
    [from, loginSuccess, navigate, onError, t]
  );

  useEffect(() => {
    let cancelled = false;
    let intervalId;

    const initGoogle = () => {
      if (cancelled || !window.google?.accounts?.id || !containerRef.current) {
        return;
      }

      setGsiReady(false);
      containerRef.current.innerHTML = '';

      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleResponse,
        auto_select: false,
        cancel_on_tap_outside: true,
        locale: mapGoogleLocale(locale),
      });

      const width = containerRef.current.parentElement?.offsetWidth || 360;
      window.google.accounts.id.renderButton(containerRef.current, {
        type: 'standard',
        theme: 'filled_black',
        size: 'large',
        shape: 'pill',
        text: gsiButtonText,
        logo_alignment: 'left',
        width: Math.min(Math.max(width, 280), 400),
      });

      if (!cancelled) {
        setGsiReady(true);
      }
    };

    if (window.google?.accounts?.id) {
      initGoogle();
    } else {
      intervalId = setInterval(() => {
        if (window.google?.accounts?.id) {
          clearInterval(intervalId);
          initGoogle();
        }
      }, 100);
    }

    return () => {
      cancelled = true;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [handleGoogleResponse, locale, gsiButtonText]);

  return (
    <div
      className={[
        'auth-google',
        gsiReady ? 'auth-google--ready' : '',
        loading ? 'auth-google--loading' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="auth-google__face" aria-hidden="true">
        <GoogleIcon />
        <span>{label}</span>
      </div>
      <div
        ref={containerRef}
        className="auth-google__gsi-overlay"
        aria-label={label}
        aria-busy={loading}
      />
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden>
      <path
        fill="#FFC107"
        d="M43.611 20.083H42V20H24v8h11.303C33.654 32.657 29.083 36 24 36c-5.514 0-10-4.486-10-10s4.486-10 10-10c2.521 0 4.817.932 6.584 2.466l6.082-6.082C33.455 9.298 28.978 7 24 7 13.507 7 5 15.507 5 26s8.507 19 19 19 19-8.507 19-19c0-1.276-.131-2.522-.389-3.717z"
      />
      <path
        fill="#FF3D00"
        d="M6.306 14.691l6.571 4.819C14.655 16.108 18.961 13 24 13c2.521 0 4.817.932 6.584 2.466l6.082-6.082C33.455 9.298 28.978 7 24 7 16.318 7 9.656 11.337 6.306 14.691z"
      />
      <path
        fill="#4CAF50"
        d="M24 45c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 36.091 26.715 37 24 37c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 45 24 45z"
      />
      <path
        fill="#1976D2"
        d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571l6.19 5.238C42.022 35.026 45 30.675 45 26c0-1.276-.131-2.522-.389-3.717z"
      />
    </svg>
  );
}
